import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { ShoppingBag, CreditCard, Truck, Mail, Eye, Package, CheckCircle2, XCircle, Clock, Search, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${window.location.origin}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const statusConfig = {
  paid: { label: 'Pagato', color: '#10B981', bg: 'rgba(16,185,129,0.1)', icon: CheckCircle2 },
  pending: { label: 'In attesa', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: Clock },
  failed: { label: 'Fallito', color: '#EF4444', bg: 'rgba(239,68,68,0.1)', icon: XCircle },
  expired: { label: 'Scaduto', color: 'var(--text-muted)', bg: 'rgba(148,163,184,0.1)', icon: XCircle },
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/orders');
        setOrders(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const openDetail = (order) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  const exportCSV = () => {
    const rows = filtered.map(o => ({
      'ID Ordine': o.order_id || '',
      'Prodotto': o.product_title || '',
      'Cliente': o.billing?.name || '',
      'Email': o.billing?.email || '',
      'Tipo': o.is_digital ? 'Digitale' : 'Fisico',
      'Prezzo Originale': o.amount?.toFixed(2) || '0.00',
      'Sconto': o.discount?.toFixed(2) || '0.00',
      'Codice Promo': o.promo_code || '',
      'Totale': o.final_price?.toFixed(2) || '0.00',
      'Valuta': o.currency?.toUpperCase() || 'EUR',
      'Metodo Pagamento': o.payment_method || '',
      'Stato': statusConfig[o.payment_status]?.label || o.payment_status || '',
      'Indirizzo Fatturazione': o.billing ? `${o.billing.address || ''}, ${o.billing.zip || ''} ${o.billing.city || ''}, ${o.billing.country || ''}`.replace(/^, | ,$/g, '') : '',
      'Tipo Documento': o.billing_type === 'fattura' ? 'Fattura' : 'Ricevuta',
      'Ragione Sociale': o.invoice?.company_name || '',
      'Partita IVA': o.invoice?.vat_number || '',
      'Codice SDI': o.invoice?.sdi_code || '',
      'Indirizzo Spedizione': !o.is_digital && o.shipping ? `${o.shipping.address || ''}, ${o.shipping.zip || ''} ${o.shipping.city || ''}, ${o.shipping.country || ''}`.replace(/^, | ,$/g, '') : '',
      'Email Consegna Digitale': o.is_digital ? (o.delivery_email || '') : '',
      'Data': o.created_at ? new Date(o.created_at).toLocaleString('it-IT') : '',
    }));
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers.map(escape).join(';'), ...rows.map(r => headers.map(h => escape(r[h])).join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ordini_quintessenza_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = orders.filter(o => {
    const matchSearch = !search || 
      o.product_title?.toLowerCase().includes(search.toLowerCase()) ||
      o.billing?.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.billing?.email?.toLowerCase().includes(search.toLowerCase()) ||
      o.order_id?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || o.payment_status === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: orders.length,
    paid: orders.filter(o => o.payment_status === 'paid').length,
    pending: orders.filter(o => o.payment_status === 'pending').length,
    revenue: orders.filter(o => o.payment_status === 'paid').reduce((sum, o) => sum + (o.final_price || 0), 0),
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div>
        <h1 className="text-2xl tracking-tight" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Ordini</h1>
        <p className="text-sm" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>Gestione ordini e pagamenti</p>
      </div>

      {/* Export button row */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={filtered.length === 0}
          className="text-xs rounded-sm gap-1.5" data-testid="export-csv-btn"
          style={{ borderColor: 'var(--border-light)', color: 'var(--text-secondary)' }}>
          <Download className="w-3.5 h-3.5" /> Esporta CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Ordini Totali', value: stats.total, icon: ShoppingBag },
          { label: 'Pagati', value: stats.paid, icon: CheckCircle2 },
          { label: 'In Attesa', value: stats.pending, icon: Clock },
          { label: 'Fatturato', value: `${stats.revenue.toFixed(2)} EUR`, icon: CreditCard },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid={`stat-${label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                <span className="text-xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{value}</span>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per nome, email, ID ordine..."
            className="pl-9 text-sm rounded-sm" data-testid="orders-search" />
        </div>
        <div className="flex gap-2" data-testid="order-status-filters">
          {['', 'paid', 'pending', 'failed'].map(s => (
            <Button key={s} variant={filterStatus === s ? 'default' : 'outline'} size="sm"
              onClick={() => setFilterStatus(s)} className="text-xs rounded-sm"
              style={filterStatus === s ? { backgroundColor: 'var(--accent)', color: '#fff' } : {}}>
              {s === '' ? 'Tutti' : statusConfig[s]?.label || s}
            </Button>
          ))}
        </div>
      </div>

      {/* Orders Table */}
      <Card className="border rounded-sm overflow-hidden" style={{ borderColor: 'var(--border-light)' }} data-testid="orders-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ fontFamily: 'var(--font-body)' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-warm)', borderBottom: '1px solid var(--border-light)' }}>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Ordine</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Prodotto</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Cliente</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Tipo</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Importo</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Stato</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}>Data</th>
                  <th className="text-left p-3 font-medium text-xs" style={{ color: 'var(--text-secondary)' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(order => {
                  const sc = statusConfig[order.payment_status] || statusConfig.pending;
                  const StatusIcon = sc.icon;
                  return (
                    <tr key={order.order_id} className="border-b last:border-b-0 hover:bg-gray-50/50 transition-colors"
                      style={{ borderColor: 'var(--border-light)' }} data-testid={`order-row-${order.order_id}`}>
                      <td className="p-3">
                        <span className="text-xs font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                          {order.order_id?.slice(0, 16)}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{order.product_title || '-'}</span>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{order.billing?.name || '-'}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{order.billing?.email || ''}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        {order.is_digital ? (
                          <Badge className="text-xs rounded-sm gap-1" style={{ backgroundColor: 'rgba(37,99,235,0.1)', color: 'var(--accent)' }}>
                            <Mail className="w-3 h-3" /> Digitale
                          </Badge>
                        ) : (
                          <Badge className="text-xs rounded-sm gap-1" style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#D97706' }}>
                            <Package className="w-3 h-3" /> Fisico
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div>
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{order.final_price?.toFixed(2)} EUR</span>
                          {order.discount > 0 && (
                            <p className="text-xs" style={{ color: 'var(--accent-success)' }}>-{order.discount?.toFixed(2)} sconto</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className="text-xs rounded-sm gap-1" style={{ backgroundColor: sc.bg, color: sc.color }}>
                          <StatusIcon className="w-3 h-3" /> {sc.label}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}
                        </span>
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(order)} data-testid={`view-order-${order.order_id}`}>
                          <Eye className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <ShoppingBag className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {orders.length === 0 ? 'Nessun ordine ancora ricevuto' : 'Nessun ordine corrisponde ai filtri'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl" style={{ fontFamily: 'var(--font-heading)' }}>Dettaglio Ordine</DialogTitle>
          </DialogHeader>
          {selectedOrder && (() => {
            const o = selectedOrder;
            const sc = statusConfig[o.payment_status] || statusConfig.pending;
            const StatusIcon = sc.icon;
            return (
              <div className="space-y-5 pt-2">
                {/* Status + ID */}
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{o.order_id}</span>
                  <Badge className="text-xs rounded-sm gap-1" style={{ backgroundColor: sc.bg, color: sc.color }}>
                    <StatusIcon className="w-3 h-3" /> {sc.label}
                  </Badge>
                </div>

                {/* Product */}
                <div className="p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', border: '1px solid var(--border-light)' }}>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{o.product_title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {o.is_digital
                      ? <><Mail className="w-3 h-3" style={{ color: 'var(--accent)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Digitale</span></>
                      : <><Truck className="w-3 h-3" style={{ color: '#D97706' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Fisico</span></>
                    }
                  </div>
                </div>

                {/* Pricing */}
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Riepilogo Prezzo</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>Prezzo originale</span>
                      <span style={{ color: 'var(--text-primary)' }}>{o.amount?.toFixed(2)} EUR</span>
                    </div>
                    {o.discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--accent-success)' }}>Sconto ({o.promo_code})</span>
                        <span style={{ color: 'var(--accent-success)' }}>-{o.discount?.toFixed(2)} EUR</span>
                      </div>
                    )}
                    <Separator className="my-1" />
                    <div className="flex justify-between text-sm font-medium">
                      <span style={{ color: 'var(--text-primary)' }}>Totale</span>
                      <span style={{ color: 'var(--text-primary)' }}>{o.final_price?.toFixed(2)} EUR</span>
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Pagamento</p>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-muted)' }}>Metodo</span>
                      <Badge variant="outline" className="text-xs rounded-sm">
                        <CreditCard className="w-3 h-3 mr-1" /> {o.payment_method === 'stripe' ? 'Carta (Stripe)' : 'PayPal'}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-muted)' }}>Session ID</span>
                      <span className="text-xs truncate max-w-[200px]" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{o.payment_session_id || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Billing */}
                {o.billing && (
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                      <CreditCard className="w-3.5 h-3.5" /> Fatturazione ({o.billing_type === 'fattura' ? 'Fattura' : 'Ricevuta'})
                    </p>
                    <div className="text-sm space-y-0.5" style={{ color: 'var(--text-primary)' }}>
                      <p>{o.billing.name}</p>
                      <p style={{ color: 'var(--text-muted)' }}>{o.billing.email}</p>
                      {o.billing.address && <p>{o.billing.address}</p>}
                      {(o.billing.city || o.billing.zip) && <p>{o.billing.zip} {o.billing.city}</p>}
                      {o.billing.country && <p>{o.billing.country}</p>}
                    </div>
                  </div>
                )}

                {/* Invoice details */}
                {o.billing_type === 'fattura' && o.invoice && (
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                      Dati Fattura
                    </p>
                    <div className="text-sm space-y-0.5 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)', color: 'var(--text-primary)' }}>
                      {o.invoice.company_name && <p><span style={{ color: 'var(--text-muted)' }}>Ragione Sociale:</span> {o.invoice.company_name}</p>}
                      {o.invoice.vat_number && <p><span style={{ color: 'var(--text-muted)' }}>P.IVA:</span> {o.invoice.vat_number}</p>}
                      {o.invoice.sdi_code && <p><span style={{ color: 'var(--text-muted)' }}>SDI:</span> {o.invoice.sdi_code}</p>}
                    </div>
                  </div>
                )}

                {/* Shipping or Digital Delivery */}
                {o.is_digital ? (
                  o.delivery_email && (
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                        <Mail className="w-3.5 h-3.5" /> Consegna Digitale
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{o.delivery_email}</p>
                    </div>
                  )
                ) : (
                  o.shipping && o.shipping.address && (
                    <div>
                      <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                        <Truck className="w-3.5 h-3.5" /> Spedizione
                      </p>
                      <div className="text-sm space-y-0.5" style={{ color: 'var(--text-primary)' }}>
                        <p>{o.shipping.name}</p>
                        <p>{o.shipping.address}</p>
                        {(o.shipping.city || o.shipping.zip) && <p>{o.shipping.zip} {o.shipping.city}</p>}
                        {o.shipping.country && <p>{o.shipping.country}</p>}
                      </div>
                    </div>
                  )
                )}

                {/* Dates */}
                <div className="text-xs space-y-1 pt-2" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  <div className="flex justify-between">
                    <span>Creato</span>
                    <span>{o.created_at ? new Date(o.created_at).toLocaleString('it-IT') : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Aggiornato</span>
                    <span>{o.updated_at ? new Date(o.updated_at).toLocaleString('it-IT') : '-'}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
